import React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useLanguage } from "@/lib/lang"
import { t } from "@/lib/i18n"

interface QuickLinksStripProps {
  level?: "gp" | "bs" | "zp"
}

export const UnifiedSamvaadServiceButton: React.FC<{ className?: string }> = ({ className }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { lang } = useLanguage()

  const title = t("tab_community_gp", lang)
  const tooltip = t("tab_community_gp_tooltip", lang)

  const handleClick = () => {
    const sp = new URLSearchParams(location.search)
    const allow = new Set(["level", "district", "block", "village"])
    for (const key of Array.from(sp.keys())) {
      if (!allow.has(key)) sp.delete(key)
    }
    const currentSearch = sp.toString() ? `?${sp.toString()}` : ""
    navigate(`/samvaad${currentSearch}`, { state: { title } })
  }

  return (
    <button
      onClick={handleClick}
      title={tooltip}
      lang={lang}
      className={className}
    >
      {title}
    </button>
  )
}

export const QuickLinksStrip: React.FC<QuickLinksStripProps> = ({ level = "gp" }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { lang } = useLanguage()

  // Define tabs with their translation keys and dynamic flags
  const baseTabs = [
    { id: "village-hub", keyBase: "tab_village_hub", isDynamic: false, path: "/village-hub" },
    { id: "weather", keyBase: "tab_weather", isDynamic: true, path: "/my-village-weather" },
    { id: "services", keyBase: "tab_services", isDynamic: false, path: "/online-services" },
    { id: "eminent", keyBase: "tab_eminent", isDynamic: true, path: "/eminent-persons" },
    { id: "community", keyBase: "tab_community_gp", isDynamic: false, path: "/samvaad" },
    { id: "reelgram", keyBase: "tab_reels", isDynamic: false, path: "/reelgram" },
    { id: "music", keyBase: "tab_music", isDynamic: false, path: "/my-music" },
    { id: "voter", keyBase: "tab_voter", isDynamic: false, path: "/voter-election" },
  ]

  const getTitle = (keyBase: string, isDynamic: boolean) => {
    if (!isDynamic) {
      return t(keyBase, lang)
    }
    // For dynamic keys, append _gp, _bs, or _zp
    return t(`${keyBase}_${level}`, lang)
  }

  const getTooltip = (keyBase: string, isDynamic: boolean) => {
    if (!isDynamic) {
      return t(`${keyBase}_tooltip`, lang)
    }
    return t(`${keyBase}_${level}_tooltip`, lang)
  }

  const handleTabClick = (path: string, title: string) => {
    const sp = new URLSearchParams(location.search)
    const allow = new Set(["level", "district", "block", "village"])
    for (const key of Array.from(sp.keys())) {
      if (!allow.has(key)) sp.delete(key)
    }
    const currentSearch = sp.toString() ? `?${sp.toString()}` : ""
    // Pass title in state for the secondary banner
    navigate(`${path}${currentSearch}`, { state: { title } })
  }

  return (
    <div id="quick-links-strip" className="w-full mt-8 mb-4">
      <nav
        className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3"
        aria-label="Quick Links"
      >
        {baseTabs.map((tab) => {
          if (tab.id === "community") {
            return (
              <UnifiedSamvaadServiceButton
                key={tab.id}
                className="
                  flex items-center justify-center
                  px-2 py-3
                  bg-card border border-border rounded-lg shadow-sm
                  text-sm font-semibold text-body
                  hover:bg-primary/5 hover:border-primary/50 hover:text-primary hover:shadow-md
                  transition-all duration-200
                  text-center leading-tight
                  h-full w-full
                  break-words hyphens-auto
                "
              />
            )
          }

          const tabTitle = getTitle(tab.keyBase, tab.isDynamic)
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.path, tabTitle)}
              title={getTooltip(tab.keyBase, tab.isDynamic)}
              lang={lang}
              className="
                flex items-center justify-center
                px-2 py-3
                bg-card border border-border rounded-lg shadow-sm
                text-sm font-semibold text-body
                hover:bg-primary/5 hover:border-primary/50 hover:text-primary hover:shadow-md
                transition-all duration-200
                text-center leading-tight
                h-full w-full
                break-words hyphens-auto
              "
            >
              {tabTitle}
            </button>
          )
        })}
      </nav>
    </div>
  )
}
